/**
 * Smart failover fetch for supabase-js `global.fetch`.
 *
 * Tries the DIRECT Supabase origin first with a short timeout. On a network /
 * timeout failure it walks down an ORDERED chain of proxy origins (a Cloudflare
 * Worker today; a VPS on another network can be appended later). It remembers
 * the working tier for the session and periodically re-probes direct to recover.
 *
 * Reads (GET/HEAD) cascade through the chain within a single request. Writes are
 * NEVER auto-retried across tiers in the same call — a timeout may mean the
 * server already applied the write, so a retry could duplicate it (e.g. a
 * duplicate order or a double token refresh). On a write failure the helper
 * latches to the next tier for the NEXT request.
 *
 * If `proxyOrigins` is empty, the helper degrades to direct-with-timeout only.
 *
 * Route-level timeouts: some server paths legitimately run far longer than the
 * default budgets (Mercato live-supplier crawls run 15–60s by design). The
 * 2026-07-01 rollout shipped without these overrides and every Mercato call
 * aborted client-side at 4.5s while the server kept completing fine
 * ("AbortError: Aborted", 2026-07-03 incident). `routeTimeouts` — defaulting to
 * DEFAULT_SLOW_ROUTES — gives those paths their real budget. First matching
 * rule wins, so keep more-specific patterns first.
 */
export interface RouteTimeoutRule {
  /** Tested against the FULL request URL. Keep more-specific patterns first. */
  pattern: RegExp;
  directTimeoutMs?: number;
  proxyTimeoutMs?: number;
  /**
   * Mark a POST route as READ-ONLY so it may cascade tiers within one call
   * like GET/HEAD. Supabase RPC reads are POSTs, so without this a slow direct
   * hop fails the whole call and latches the session to the proxy. ONLY for
   * routes with no write side effects — anchor the pattern to the exact RPC
   * path so a similarly-named write can never inherit it.
   */
  idempotentPost?: boolean;
}

// Known-slow paths. Budgets sized from observed server-side durations
// (mercato-crawl-prices: 19–21s typical, live_suppliers up to ~60s;
// browse_mercato_catalog ~4s until the trigram index landed). Edge functions
// in general are long-running by DESIGN on this project — pos-proxy PC
// roundtrips through the tunnel, sync-pos-live full cycles (35s+),
// send-purchase-notifications WhatsApp batches — and some
// are WRITES (create-supplier-payment) where a premature client abort invites
// a manual retry → duplicate (2026-07-03 investigation). So EVERY
// /functions/v1/ call gets a 30s floor; the tight 4.5s/9s default remains for
// PostgREST + auth, which was the original infinite-spinner target. A budget
// still bounds a truly dead route — it just stops killing healthy-but-slow
// requests. First matching rule wins; keep more-specific patterns first.
export const DEFAULT_SLOW_ROUTES: RouteTimeoutRule[] = [
  { pattern: /\/functions\/v1\/mercato-crawl-prices/, directTimeoutMs: 65000, proxyTimeoutMs: 65000 },
  // enrich-search-data chains two AI providers: up to 15s Gemini + 15s OpenAI
  // when the primary is failing (exactly the gemini-2.0-flash retirement that
  // took keyword generation down). The generic 30s floor below would abort that
  // chain mid-fallback and report a transport failure instead of the function's
  // own explanation, so this route needs its own budget ABOVE the caller's 45s.
  // ANCHORED so a prefix route (enrich-search-data-preview) or this path merely
  // appearing in a query param cannot inherit the long budget — same discipline
  // as the anchored RPC patterns below.
  { pattern: /^https?:\/\/[^/?#]+\/functions\/v1\/enrich-search-data(?:\?[^#]*)?$/, directTimeoutMs: 50000, proxyTimeoutMs: 50000 },
  { pattern: /\/functions\/v1\//, directTimeoutMs: 30000, proxyTimeoutMs: 30000 },
  // Staff fast-browse wrapper (TASK-008) — the dashboard's useBrowseMercato/useMercatoSearch
  // migrated onto it. ANCHORED to the exact path so a write RPC merely carrying this string in
  // a query param cannot inherit the long budget. (This helper never cascades a POST — line
  // ~122 treats only GET/HEAD as idempotent — so this is a timeout budget only.)
  { pattern: /^https?:\/\/[^/?#]+\/rest\/v1\/rpc\/browse_mercato_catalog_staff(?:\?[^#]*)?$/, directTimeoutMs: 20000, proxyTimeoutMs: 25000 },
  // Base browse RPC — PRESERVED (rollback + non-staff/server callers). ANCHORED.
  { pattern: /^https?:\/\/[^/?#]+\/rest\/v1\/rpc\/browse_mercato_catalog(?:\?[^#]*)?$/, directTimeoutMs: 20000, proxyTimeoutMs: 25000 },
  // Mercato linking RPCs. link_mercato_product is a WRITE that runs a sibling
  // auto-link sweep + availability sync; a premature abort commits server-side
  // while the client shows "AbortError" and every retry then trips the RPC's
  // already-linked guard (2026-07-19 incident — exact repeat of the 2026-07-03
  // class documented above). mercato_search_catalog measured ~5s on multi-term
  // queries pre-fix; it is a pure read, so it cascades tiers via idempotentPost.
  // Patterns are anchored to the exact RPC path so nothing else inherits them.
  // Pricing recomputes walk thousands of rows one at a time (Mercato ~3,437,
  // POS ~25,770) and cannot finish inside the 4.5s/9s default — the RPC now
  // carries SET statement_timeout='180s' server-side, so the client budget must
  // match or the UI aborts a run that is still committing. These are WRITES, so
  // idempotentPost stays off: a premature abort must never cascade into a second
  // run (same class as the 2026-07-03 / 2026-07-19 duplicate-write incidents).
  { pattern: /^https?:\/\/[^/?#]+\/rest\/v1\/rpc\/recompute_(?:source_prices|all_prices)(?:\?[^#]*)?$/, directTimeoutMs: 190000, proxyTimeoutMs: 190000 },
  { pattern: /^https?:\/\/[^/?#]+\/rest\/v1\/rpc\/(?:link|unlink)_mercato_product(?:\?[^#]*)?$/, directTimeoutMs: 20000, proxyTimeoutMs: 20000 },
  { pattern: /^https?:\/\/[^/?#]+\/rest\/v1\/rpc\/mercato_search_catalog(?:\?[^#]*)?$/, directTimeoutMs: 20000, proxyTimeoutMs: 25000, idempotentPost: true },
];

export interface FailoverFetchOptions {
  directOrigin: string;
  proxyOrigins: string[];
  directTimeoutMs?: number;
  proxyTimeoutMs?: number;
  probeIntervalMs?: number;
  /** Per-route timeout overrides. Defaults to DEFAULT_SLOW_ROUTES; pass [] to disable. */
  routeTimeouts?: RouteTimeoutRule[];
  fetchImpl?: typeof fetch;
  now?: () => number;
  log?: (m: string) => void;
}

type FetchInput = Parameters<typeof fetch>[0];
type FetchInit = Parameters<typeof fetch>[1];

export function makeFailoverFetch(opts: FailoverFetchOptions): typeof fetch {
  const directTimeoutMs = opts.directTimeoutMs ?? 4500;
  const proxyTimeoutMs = opts.proxyTimeoutMs ?? 9000;
  const probeIntervalMs = opts.probeIntervalMs ?? 60000;
  const routeTimeouts = opts.routeTimeouts ?? DEFAULT_SLOW_ROUTES;
  const baseFetch = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const now = opts.now ?? Date.now;
  const { directOrigin, proxyOrigins, log } = opts;

  // tier 0 = direct, tiers 1..N = proxies in order
  const tiers = [directOrigin, ...proxyOrigins];

  let preferred = 0; // index into `tiers` we currently start from
  // Advances ONLY when the direct tier is actually attempted — never on a proxy
  // failure. Drives the re-probe cadence. (The old code reused `switchedAt`, which
  // was reset on every failed cascade, so under a 2s poll the 60s probe window
  // never elapsed and a broken-proxy session never fell back to direct.)
  let lastDirectProbeAt = 0;

  const urlOf = (input: FetchInput): string =>
    typeof input === 'string' ? input : input instanceof URL ? input.toString() : (input as Request).url;

  const methodOf = (input: FetchInput, init?: FetchInit): string => {
    const m = init?.method ?? (typeof input === 'object' && input !== null && 'method' in input ? (input as Request).method : undefined);
    return (m ?? 'GET').toUpperCase();
  };

  const swapOrigin = (url: string, origin: string): string => {
    const u = new URL(url);
    const o = new URL(origin);
    u.protocol = o.protocol;
    u.host = o.host;
    return u.toString();
  };

  const isRouteError = (err: unknown): boolean =>
    err instanceof Error ? err.name === 'AbortError' || err.name === 'TypeError' || err.name === 'FetchError' : true;

  const timedFetch = async (url: string, init: FetchInit, ms: number): Promise<Response> => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ms);
    const external = init?.signal;
    if (external) {
      if (external.aborted) controller.abort();
      else external.addEventListener('abort', () => controller.abort(), { once: true });
    }
    try {
      return await baseFetch(url, { ...init, signal: controller.signal });
    } finally {
      clearTimeout(timer);
    }
  };

  const failoverFetch = async (input: FetchInput, init?: FetchInit): Promise<Response> => {
    const url = urlOf(input);
    const method = methodOf(input, init);
    const rule = routeTimeouts.find((r) => r.pattern.test(url));
    const idempotent =
      method === 'GET' ||
      method === 'HEAD' ||
      (method === 'POST' && rule?.idempotentPost === true);
    const tierTimeout = (tier: number): number =>
      tier === 0 ? rule?.directTimeoutMs ?? directTimeoutMs : rule?.proxyTimeoutMs ?? proxyTimeoutMs;

    // No proxies configured → direct only, with timeout.
    if (tiers.length === 1) {
      return timedFetch(swapOrigin(url, directOrigin), init ?? {}, tierTimeout(0));
    }

    // Build the ordered list of tiers to attempt for THIS request.
    //
    // Reads (idempotent) may traverse every tier in one call. We start at the
    // preferred tier (the fast path on a degraded ISP) but ALWAYS wrap back to the
    // lower tiers — INCLUDING direct — so a read never fails without having tried
    // direct. That is the self-heal for the case where the *proxy itself* is the
    // broken tier: on 2026-07-17 the Cloudflare proxy CORS-rejected every
    // PostgREST request, and the old code — latched to the proxy — retried only
    // the proxy (every 2s from the Send-to-POS poll) and never fell back to the
    // healthy direct route, trapping the whole session for the full 6-min timeout.
    //
    // The re-probe cadence uses `lastDirectProbeAt`, advanced only when the direct
    // tier is actually attempted, so a busy proxy-failure loop can no longer starve
    // the periodic direct probe.
    const timeToProbeDirect = preferred > 0 && now() - lastDirectProbeAt > probeIntervalMs;

    let order: number[];
    if (idempotent) {
      const rotation: number[] = [];
      for (let i = preferred; i < tiers.length; i++) rotation.push(i);
      for (let i = 0; i < preferred; i++) rotation.push(i);
      order = timeToProbeDirect ? [0, ...rotation.filter((t) => t !== 0)] : rotation;
    } else {
      // Writes: attempt exactly ONE tier (the preferred one), never cascade in the
      // same call — a timeout may mean the write already applied. On failure, latch
      // to the next tier for the NEXT call.
      order = [preferred];
    }

    let lastErr: unknown;
    for (const i of order) {
      if (i === 0) lastDirectProbeAt = now();
      try {
        const res = await timedFetch(swapOrigin(url, tiers[i]), init ?? {}, tierTimeout(i));
        if (i !== preferred) {
          preferred = i;
          log?.(i === 0 ? '[supabase] direct route recovered' : `[supabase] using fallback tier ${i}`);
        }
        return res;
      } catch (err) {
        if (!isRouteError(err)) throw err; // real HTTP error surfaced to caller
        lastErr = err;
        if (!idempotent) {
          // WRITE SAFETY: never try another tier in the same call. Advance for next time.
          preferred = Math.min(i + 1, tiers.length - 1);
          throw err;
        }
        // read: fall through to next tier in `order`
      }
    }
    // Every read tier failed. Advance the preferred tier for the next call, but
    // leave `lastDirectProbeAt` intact so the probe cadence is preserved.
    preferred = Math.min(preferred + 1, tiers.length - 1);
    throw lastErr;
  };

  // Cast: RN's lib.dom `fetch` type is overloaded; our single-signature wrapper is
  // runtime-compatible but not structurally assignable to the overload set.
  return failoverFetch as unknown as typeof fetch;
}
